import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ProcessStepItem from './ProcessStepItem';
import { PROCESS_STEP_STATUS, PROCESS_STEP_STATUSES } from './constants';
import { resolveStepStatus, stepKey } from './helpers';

// Design System — ProcessStep
// A vertical, numbered sequence: each step has a badge joined by a connector line, a
// title, an optional error line and free content. `steps` drives the rows; `doneLabel`
// appends the closing "Done!" row (numbered after the last step) when given. Fully
// presentational: the consumer owns the data and passes each step's content as a node.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline).

const ProcessStep = ({ steps = [], doneLabel, startAt = 1, ariaLabel, className, ...rest }) => {
    const rows = steps.filter(Boolean);
    const hasDone = doneLabel != null && doneLabel !== '';

    return (
        <ol className={classNames('ds-process-step', className)} aria-label={ariaLabel} {...rest}>
            {rows.map((step, index) => (
                <ProcessStepItem
                    key={stepKey(step, index)}
                    number={startAt + index}
                    title={step.title}
                    status={resolveStepStatus(step.status)}
                    error={step.error}
                    isLast={!hasDone && index === rows.length - 1}
                    className={step.className}
                >
                    {step.content}
                </ProcessStepItem>
            ))}
            {hasDone && (
                <ProcessStepItem
                    key="done"
                    number={startAt + rows.length}
                    status={PROCESS_STEP_STATUS.DONE}
                    isLast
                    className="ds-process-step__item--closing"
                >
                    <span className="ds-process-step__done-label">{doneLabel}</span>
                </ProcessStepItem>
            )}
        </ol>
    );
};

const stepShape = PropTypes.shape({
    /** Stable key; falls back to the position */
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.node,
    /** Step body */
    content: PropTypes.node,
    /** Badge state: default / completed / done */
    status: PropTypes.oneOf([...PROCESS_STEP_STATUSES]),
    /** Error text shown under the title */
    error: PropTypes.node,
    className: PropTypes.string,
});

ProcessStep.propTypes = {
    /** Ordered steps */
    steps: PropTypes.arrayOf(stepShape),
    /** Text of the closing row; omit to render no closing row */
    doneLabel: PropTypes.node,
    /** Number of the first badge */
    startAt: PropTypes.number,
    /** Accessible name of the list */
    ariaLabel: PropTypes.string,
    /** Extra class on the list */
    className: PropTypes.string,
};

export { ProcessStepItem, PROCESS_STEP_STATUS, PROCESS_STEP_STATUSES, resolveStepStatus, stepKey };
export default ProcessStep;
